/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 9/6/2023
 */

import React from "react";
import { ArrowForward, CheckCircle, DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";
import { Button, Grid, Paper, Typography } from "@mui/material";
import styles from "./LoginPage.module.css";
import { Link } from "react-router-dom";

const LoginPage = ({ themeMode = "light", onThemeToggle }) => {
  const toggleTheme = () => {
    if (onThemeToggle) {
      onThemeToggle();
    }
  };

  return (
    <div className={`${styles.pageShell} ${themeMode === "dark" ? styles.dark : ""}`}>
      <header className={`${styles.topNav} ${themeMode === "dark" ? styles.darkNav : ""}`}>
        <div className={styles.brandBlock}>
          <div className={styles.brandMark}>N</div>
          <div>
            <Typography className={styles.brandTitle}>N-ACH POS</Typography>
            <Typography className={styles.brandSubtitle}>SMARTER CHECKOUTS. FASTER OPERATIONS.</Typography>
          </div>
        </div>
        <div className={styles.navActions}>
          <Button className={`${styles.iconAction} ${themeMode === "dark" ? styles.darkButton : ""}`} aria-label={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"} onClick={toggleTheme}>
            {themeMode === "dark" ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
          </Button>
          <Link to="/login" className={`${styles.navLinkBtn} ${themeMode === "dark" ? styles.darkNavLink : ""}`}>Sign In</Link>
          <Link to="/login" className={`${styles.navPrimaryBtn} ${themeMode === "dark" ? styles.darkPrimaryBtn : ""}`}>Get Started</Link>
        </div>
      </header>

      <main className={styles.heroSection}>
        <Grid container className={styles.gridContainer}>
          <Grid item xs={12} lg={6}>
            <Typography className={styles.eyebrow}>ONE DASHBOARD. EVERY CHECKOUT.</Typography>
            <Typography className={styles.welcome}>
              Turn every counter sale into
              <span className={styles.accentWord}> a completed bill.</span>
            </Typography>
            <Typography className={styles.subTopic}>
              A retail-first POS platform to manage billing, stock, discounts, taxes, and branch performance from one cashier dashboard.
            </Typography>

            <div className={styles.ctaRow}>
              <Link to="/login" className={styles.heroPrimaryBtn}>
                Start billing faster <ArrowForward fontSize="small" />
              </Link>
              <Link to="/login" className={styles.heroSecondaryBtn}>See how it works</Link>
            </div>

            <div className={styles.trustRow}>
              <span><CheckCircle fontSize="inherit" /> No credit card</span>
              <span><CheckCircle fontSize="inherit" /> Set up in minutes</span>
            </div>
          </Grid>

          <Grid item xs={12} lg={6} className={styles.visualCol}>
            <div className={styles.visualWrap}>
              <div className={styles.visualCircle} />
              <div className={styles.posCardMain}>
                <Typography className={styles.cardTitle}>Today's Checkout</Typography>
                <Typography className={styles.cardValue}>$12,840</Typography>
                <Typography className={styles.cardSub}>+18% vs yesterday</Typography>
                <div className={styles.barRow}>
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
              <div className={styles.floatCardTop}>
                <Typography className={styles.floatMain}>Bill #POS-4021</Typography>
                <Typography className={styles.floatSub}>Cash received</Typography>
              </div>
              <div className={styles.floatCardBottom}>
                <Typography className={styles.floatMain}>Register sync</Typography>
                <Typography className={styles.floatSub}>Updated 42 SKUs</Typography>
              </div>
            </div>
          </Grid>
        </Grid>

        <Paper className={styles.metricsStrip} elevation={0}>
          <div>
            <Typography className={styles.metricLabel}>Orders / day</Typography>
            <Typography className={styles.metricValue}>1.8K</Typography>
          </div>
          <div>
            <Typography className={styles.metricLabel}>Avg checkout time</Typography>
            <Typography className={styles.metricValue}>43s</Typography>
          </div>
          <div>
            <Typography className={styles.metricLabel}>Inventory accuracy</Typography>
            <Typography className={styles.metricValue}>99.4%</Typography>
          </div>
          <div>
            <Typography className={styles.metricLabel}>Active branches</Typography>
            <Typography className={styles.metricValue}>24</Typography>
          </div>
        </Paper>
      </main>

      <section className={styles.valueSection}>
        <Typography className={styles.valueEyebrow}>EVERYTHING YOUR TEAM NEEDS</Typography>
        <Typography className={styles.valueTitle}>Less friction. More completed checkouts.</Typography>
        <Typography className={styles.valueSubTitle}>
          Keep operations simple from item scan to printed receipt.
        </Typography>

        <div className={styles.featureGridSection}>
          <article className={styles.featureItem}>
            <div className={`${styles.featureIconBox} ${styles.featureIconAmber}`}>01</div>
            <Typography className={styles.featureTitle}>Bill every customer quickly</Typography>
            <Typography className={styles.featureDescription}>
              Track bills, line items, and payment status in real time with fewer manual steps.
            </Typography>
            <Link className={styles.featureLink} to="/login">Explore feature</Link>
          </article>

          <article className={styles.featureItem}>
            <div className={`${styles.featureIconBox} ${styles.featureIconPink}`}>02</div>
            <Typography className={styles.featureTitle}>Speed up counter checkout</Typography>
            <Typography className={styles.featureDescription}>
              Apply discounts, taxes, and payment modes instantly for shorter queue times.
            </Typography>
            <Link className={styles.featureLink} to="/login">Explore feature</Link>
          </article>

          <article className={styles.featureItem}>
            <div className={`${styles.featureIconBox} ${styles.featureIconBlue}`}>03</div>
            <Typography className={styles.featureTitle}>Monitor inventory movement</Typography>
            <Typography className={styles.featureDescription}>
              Use branch-level dashboards to track fast-moving items and stock alerts.
            </Typography>
            <Link className={styles.featureLink} to="/login">Explore feature</Link>
          </article>
        </div>
      </section>

      <section className={styles.finalCtaSection}>
        <div className={styles.finalCtaCard}>
          <div>
            <Typography className={styles.finalCtaEyebrow}>ONE PLATFORM. END-TO-END SALES.</Typography>
            <Typography className={styles.finalCtaTitle}>Set up your POS terminals today.</Typography>
            <Typography className={styles.finalCtaText}>Onboard your cashiers and start processing secure in-store payments in minutes.</Typography>
          </div>
          <Link to="/login" className={styles.finalCtaButton}>Open POS</Link>
        </div>
      </section>

      <footer className={styles.pageFooter}>
        <Typography>N-ACH POS</Typography>
        <Typography>© 2026 N-ACH CRM Powered by N-ACH</Typography>
      </footer>
    </div>
  );
};

export default LoginPage;
